import { FC } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Button,
  Typography,
  Badge,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconX } from "@tabler/icons-react";
import { LocalAgentVersion } from "../hooks/useLocalAgents";

interface LocalVersionComparisonDialogProps {
  agentName: string;
  version1: number;
  version2: number;
  versions: LocalAgentVersion[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const LocalVersionComparisonDialog: FC<LocalVersionComparisonDialogProps> = ({
  agentName,
  version1,
  version2,
  versions,
  open,
  onOpenChange,
}) => {
  // Find the two versions to compare
  const v1Data = versions.find(v => v.version === version1);
  const v2Data = versions.find(v => v.version === version2);

  // Helper to check if field changed
  const hasChanged = (field1: unknown, field2: unknown): boolean => {
    if (Array.isArray(field1) && Array.isArray(field2)) {
      return JSON.stringify(field1) !== JSON.stringify(field2);
    }
    return field1 !== field2;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="al-max-w-6xl al-max-h-[90vh] al-overflow-hidden al-flex al-flex-col">
        <DialogHeader>
          <Stack direction="row" justify="between" align="center">
            <div>
              <DialogTitle className="al-text-xl">Version Comparison</DialogTitle>
              <Typography className="al-text-gray-500 al-mt-1">
                {agentName}: v{version1} vs v{version2}
              </Typography>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
            >
              <IconX className="al-w-4 al-h-4" />
            </Button>
          </Stack>
        </DialogHeader>

        {!v1Data || !v2Data ? (
          <Stack direction="row" justify="center" align="center" className="al-py-12">
            <Typography className="al-text-gray-500">
              Version data not found
            </Typography>
          </Stack>
        ) : (
          <div className="al-overflow-y-auto al-flex-1">
            <div className="al-grid al-grid-cols-2 al-gap-4">
              {/* Version 1 Column */}
              <div>
                <div className="al-bg-purple-50 al-border al-border-purple-200 al-p-3 al-rounded-t-lg">
                  <Stack direction="row" gap="sm" align="center">
                    <Badge className="!al-bg-purple-600 !al-text-white">
                      v{version1}
                    </Badge>
                    <Typography variant="body" weight="medium">
                      Current Version
                    </Typography>
                  </Stack>
                </div>
                <div className="al-border al-border-t-0 al-border-purple-200 al-rounded-b-lg al-p-4 al-space-y-4">
                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      Name
                    </Typography>
                    <div className={`al-p-2 al-rounded ${hasChanged(v1Data.name, v2Data.name) ? "al-bg-yellow-50" : ""}`}>
                      <Typography>{v1Data.name || "N/A"}</Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      Description
                    </Typography>
                    <div className={`al-p-2 al-rounded ${hasChanged(v1Data.description, v2Data.description) ? "al-bg-yellow-50" : ""}`}>
                      <Typography>{v1Data.description || "N/A"}</Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      Model
                    </Typography>
                    <div className={`al-p-2 al-rounded ${hasChanged(v1Data.model, v2Data.model) ? "al-bg-yellow-50" : ""}`}>
                      <Typography className="al-font-mono">{v1Data.model || "N/A"}</Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      Created At
                    </Typography>
                    <div className="al-p-2 al-rounded">
                      <Typography>{new Date(v1Data.created_at).toLocaleString()}</Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      System Prompt
                    </Typography>
                    <div className={`al-p-3 al-rounded al-border ${hasChanged(v1Data.prompt, v2Data.prompt) ? "al-bg-yellow-50 al-border-yellow-300" : "al-bg-gray-50 al-border-gray-200"}`}>
                      <pre className="al-text-xs al-whitespace-pre-wrap al-font-mono al-text-gray-800 al-max-h-64 al-overflow-y-auto">
                        {v1Data.prompt || "N/A"}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Version 2 Column */}
              <div>
                <div className="al-bg-blue-50 al-border al-border-blue-200 al-p-3 al-rounded-t-lg">
                  <Stack direction="row" gap="sm" align="center">
                    <Badge className="!al-bg-blue-600 !al-text-white">
                      v{version2}
                    </Badge>
                    <Typography variant="body" weight="medium">
                      Comparing Version
                    </Typography>
                  </Stack>
                </div>
                <div className="al-border al-border-t-0 al-border-blue-200 al-rounded-b-lg al-p-4 al-space-y-4">
                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      Name
                    </Typography>
                    <div className={`al-p-2 al-rounded ${hasChanged(v1Data.name, v2Data.name) ? "al-bg-yellow-50" : ""}`}>
                      <Typography>{v2Data.name || "N/A"}</Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      Description
                    </Typography>
                    <div className={`al-p-2 al-rounded ${hasChanged(v1Data.description, v2Data.description) ? "al-bg-yellow-50" : ""}`}>
                      <Typography>{v2Data.description || "N/A"}</Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      Model
                    </Typography>
                    <div className={`al-p-2 al-rounded ${hasChanged(v1Data.model, v2Data.model) ? "al-bg-yellow-50" : ""}`}>
                      <Typography className="al-font-mono">{v2Data.model || "N/A"}</Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      Created At
                    </Typography>
                    <div className="al-p-2 al-rounded">
                      <Typography>{new Date(v2Data.created_at).toLocaleString()}</Typography>
                    </div>
                  </div>

                  <div>
                    <Typography variant="body" weight="medium" className="al-text-gray-700 al-uppercase al-mb-2">
                      System Prompt
                    </Typography>
                    <div className={`al-p-3 al-rounded al-border ${hasChanged(v1Data.prompt, v2Data.prompt) ? "al-bg-yellow-50 al-border-yellow-300" : "al-bg-gray-50 al-border-gray-200"}`}>
                      <pre className="al-text-xs al-whitespace-pre-wrap al-font-mono al-text-gray-800 al-max-h-64 al-overflow-y-auto">
                        {v2Data.prompt || "N/A"}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Legend */}
            <div className="al-mt-4 al-p-3 al-bg-gray-50 al-border al-border-gray-200 al-rounded-lg">
              <Stack direction="row" gap="md" align="center">
                <Typography variant="body" weight="medium" className="al-text-gray-700">
                  Legend:
                </Typography>
                <Stack direction="row" gap="sm" align="center">
                  <div className="al-w-4 al-h-4 al-bg-yellow-50 al-border al-border-yellow-300 al-rounded"></div>
                  <Typography className="al-text-gray-600">
                    Changed
                  </Typography>
                </Stack>
              </Stack>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
